import { Grid, Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { OLModal } from "components/Tooltips/OLModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import { memo, useEffect, useState } from "react";
import { AttributeType, Draft, SchemaAttribute } from "types/customEvents";
import { getRawAttribute, markEditResolved } from "../utils";
import { AttributeReadOnly } from "./AttributeReadOnly";
import { useStyles } from "./Attributes";
import { JsonEditor } from "./JsonEditor/JsonEditor";

interface Props {
  remove: (index: number) => void;
  index: number;
  attribute: AttributeType;
  setFieldValue: (field: string, value: any) => void;
  add: (attribute: AttributeType) => void;
  setFieldError: (field: string, errorMsg?: any) => void;
  error?: any;
  isComplexAttribute: boolean;
}

export const Attribute = memo(
  ({
    remove,
    index,
    attribute,
    setFieldValue,
    add,
    setFieldError,
    error,
    isComplexAttribute,
  }: Props) => {
    const { message } = useMessage();
    const classes = useStyles();
    const [isPreviewCodeOpen, setIsPreviewCodeOpen] = useState(false);
    const { isSaved, isEditing } = attribute;
    const fieldName = isEditing
      ? `schema.fields[${index}].draft`
      : `schema.fields[${index}]`;

    const rawAttributeData = getRawAttribute(attribute);

    useEffect(() => {
      setIsPreviewCodeOpen(isComplexAttribute ? !!attribute.isEditing : false);
    }, [attribute, isComplexAttribute]);

    return isEditing ? (
      <div
        className={classes.attribute}
        data-attr-edit={`schema.fields-${index}`}
      >
        <OLModal
          disableEscapeKeyDown
          open={!!isPreviewCodeOpen}
          onClose={() => {
            setIsPreviewCodeOpen(false);

            if (isComplexAttribute) {
              if (isSaved) {
                return markEditResolved(`schema.fields-${index}`);
              }
              remove(index);
            }
          }}
          modalWidth={550}
        >
          <JsonEditor
            json={{ ...rawAttributeData }}
            closeModal={() => {
              if (isComplexAttribute) {
                remove(index);
              }
              setIsPreviewCodeOpen(false);
            }}
            setFieldValue={(json: any) => {
              markEditResolved(`schema.fields-${index}`);

              setFieldValue(`schema.fields[${index}]`, {
                ...json,
                isEditing: false,
                isSaved: true,
              });
            }}
          />
        </OLModal>
        <div className={classes.titleWrapper}>
          <Typography mb={12}>
            {isSaved
              ? message({ id: "editAttribute" })
              : message({
                  id: "addNewAttribute",
                })}
          </Typography>
          <span
            className={classes.previewCode}
            onClick={() => setIsPreviewCodeOpen(true)}
          >
            {message({ id: "previewCode" })}
          </span>
        </div>
        <Grid container spacing={12}>
          <Grid item xs={4}>
            <TextInput
              label={
                <RequiredFieldLabel
                  text={message({
                    id: "attributeSystemIdentifier",
                  })}
                />
              }
              name={`${fieldName}.name`}
              className={classes.input}
              data-test-id="attribute-name"
              InputProps={{
                endAdornment: (
                  <Tooltip
                    text={message({
                      id: "attributeSystemIdentifierInfo",
                    })}
                  >
                    <InfoCircleIcon />
                  </Tooltip>
                ),
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <SelectInput
              items={[
                {
                  id: "boolean",
                  label: message({
                    id: "boolean",
                  }),
                },
                {
                  id: "datetime",
                  label: message({
                    id: "datetime",
                  }),
                },
                {
                  id: "number",
                  label: message({
                    id: "number",
                  }),
                },
                {
                  id: "text",
                  label: message({
                    id: "text",
                  }),
                },
              ]}
              label={
                <RequiredFieldLabel
                  text={message({
                    id: "attributeType",
                  })}
                />
              }
              name={`${fieldName}.type`}
              data-test-id="attribute-type"
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              label={message({
                id: "attributeName",
              })}
              name={`${fieldName}.description`}
              className={classes.input}
              data-test-id="attribute-description"
              InputProps={{
                endAdornment: (
                  <Tooltip
                    text={message({
                      id: "attributeNameInfo",
                    })}
                  >
                    <InfoCircleIcon />
                  </Tooltip>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <div className={classes.buttonWrapper}>
              <OLButton
                data-test-id="attribute-cancel"
                onClick={() => {
                  if (isSaved) {
                    markEditResolved(`schema.fields-${index}`);

                    setFieldValue(`schema.fields[${index}]`, {
                      name: attribute.name,
                      description: attribute.description,
                      type: attribute.type,
                      isEditing: false,
                      isSaved: true,
                    });
                  } else {
                    remove(index);
                  }
                }}
                size="small"
              >
                {isSaved ? message({ id: "close" }) : message({ id: "cancel" })}
              </OLButton>
              <OLButton
                data-test-id="attribute-save"
                onClick={() => {
                  markEditResolved(`schema.fields-${index}`);

                  // Save value and remove draft key
                  setFieldValue(`schema.fields[${index}]`, {
                    name: (attribute.draft as Draft).name,
                    description: (attribute.draft as Draft).description,
                    type: (attribute.draft as Draft).type,
                    isEditing: false,
                    isSaved: true,
                  });
                  // Reset schema err
                  setFieldError(`schema.fields[${index}]`, undefined);
                }}
                variant="contained"
                size="small"
              >
                {message({ id: "saveAttribute" })}
              </OLButton>
            </div>
          </Grid>
        </Grid>
      </div>
    ) : (
      <AttributeReadOnly
        index={index}
        name={attribute.name}
        description={attribute.description}
        type={attribute.type}
        remove={remove}
        add={add}
        error={error}
        edit={() => {
          // Move errors to draft view
          setFieldError(`schema.fields[${index}].draft`, error);

          setFieldValue(`schema.fields[${index}]`, {
            name: attribute.name,
            description: attribute.description,
            type: attribute.type,
            isSaved: attribute.isSaved,
            isEditing: true,
            draft: {
              name: attribute.name,
              description: attribute.description,
              type: attribute.type,
            },
          });
        }}
        isComplexAttribute={isComplexAttribute}
        attributeData={rawAttributeData as SchemaAttribute}
        setFieldValue={(value: any) =>
          setFieldValue(`schema.fields[${index}]`, value)
        }
      />
    );
  },
);
